import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * MasonryGrid — Turbo 360 "Cinema" TRUE varied-height masonry.
 *
 * Uses column-DISTRIBUTION masonry (a flex row of column stacks) with
 * natural-height images, so photos keep their real aspect ratios and pack into
 * balanced columns. This avoids the iOS Safari multi-column `break-inside` bug
 * and packs gap-free. (Thumbnails are now generated aspect-preserving — no
 * white square padding — so there are no baked-in white bars.)
 *
 * 3px gaps, responsive 6→2 columns. Hover reveals a mono filename +
 * favourite/download actions. Supports AI-pick badges, face-match confidence
 * badges, infinite scroll, the paywall right-click guard and branded frame
 * overlays (frameUrlFor). theme="light" for paper surfaces.
 */
const colsForWidth = (w) => {
  if (w <= 520) return 2;
  if (w <= 760) return 3;
  if (w <= 1100) return 4;
  if (w <= 1366) return 5;
  return 6;
};

export function MasonryGrid({
  photos,
  onPhotoClick,
  onDownload,
  onFavorite,
  favoritedPhotoIds = new Set(),
  backendUrl,
  onLoadMore,
  hasMore = false,
  confidenceScores = {},
  showConfidence = false,
  paywallEnabled = false,
  theme = 'dark',
  showActions = true,
  protectImages = false,
  frameUrlFor,
  onImageLoad,
}) {
  // Discourage casual saving (right-click "Save image", drag-to-desktop).
  const protect = paywallEnabled || protectImages;
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [cols, setCols] = useState(() => (typeof window !== 'undefined' ? colsForWidth(window.innerWidth) : 4));
  const sentinelRef = useRef(null);

  useEffect(() => {
    const onResize = () => setCols(colsForWidth(window.innerWidth));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const getPhotoUrl = (photo, type = 'thumbnail') => {
    const url = type === 'thumbnail'
      ? (photo.thumbnail_url || photo.thumbnail_path)
      : (photo.preview_url || photo.preview_path);
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${backendUrl}/api/photos/file/${type === 'thumbnail' ? 'thumbnails' : 'previews'}/${url.split('/').pop()}`;
  };

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!onLoadMore || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) onLoadMore(); },
      { rootMargin: '300px' }
    );
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore]);

  const handleImageLoad = useCallback((photoId) => {
    setLoadedImages((prev) => new Set([...prev, photoId]));
  }, []);

  // face-match confidence badge — Best (green) / Good (teal) / Possible (amber)
  const confidenceBadge = (photoId) => {
    if (!showConfidence) return null;
    const score = confidenceScores[photoId];
    if (!score) return null;
    let bg = '#FEF3C7', fg = '#92400E', label = 'Possible';
    if (score >= 85) { bg = '#DCFCE7'; fg = '#15803D'; label = 'Best match'; }
    else if (score >= 70) { bg = '#CFFAFE'; fg = '#0E7490'; label = 'Good'; }
    return (
      <div
        className="font-tiq-mono"
        style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', background: bg, color: fg, padding: '3px 7px', borderRadius: 5 }}
      >
        {label} · {Math.round(score)}%
      </div>
    );
  };

  const overlayBg = 'linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.72))';

  // distribute photos round-robin into `cols` column stacks
  const columns = Array.from({ length: cols }, () => []);
  photos.forEach((photo, index) => { columns[index % cols].push({ photo, index }); });

  // Plain render function (NOT a nested component) so tiles reconcile in place
  // instead of remounting on each image-load state update.
  const renderTile = (photo, index) => {
    const isLoaded = loadedImages.has(photo.id);
    const isFav = favoritedPhotoIds.has(photo.id);
    const isTop = photo.is_ai_top30 || photo.ai_top30 || photo.is_top;
    return (
      <div
        key={photo.id}
        className="group"
        style={{
          position: 'relative',
          cursor: 'pointer',
          overflow: 'hidden',
          backgroundColor: theme === 'light' ? '#E2DED6' : '#1a1a1c',
        }}
        onClick={() => onPhotoClick(photo, index)}
      >
        {isTop && (
          <div className="tiq-ai-pill font-tiq-mono" style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: '#fff', padding: '3px 7px', borderRadius: 5 }}>
            AI PICK
          </div>
        )}
        {confidenceBadge(photo.id)}

        <img
          src={getPhotoUrl(photo, 'thumbnail')}
          alt={photo.filename}
          loading="lazy"
          onLoad={(e) => {
            handleImageLoad(photo.id);
            if (onImageLoad) onImageLoad(photo.id, e);
          }}
          onContextMenu={protect ? (e) => e.preventDefault() : undefined}
          draggable={protect ? false : undefined}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s var(--tiq-ease)',
            ...(protect ? { WebkitTouchCallout: 'none', userSelect: 'none' } : {}),
          }}
        />

        {/* Branded frame overlay — the tile IS the photo's natural box (natural-height
            masonry), so stretching the overlay to the tile matches the server composite,
            which resizes the frame to the photo's exact dimensions. */}
        {frameUrlFor && frameUrlFor(photo) && (
          <img
            src={frameUrlFor(photo)}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              pointerEvents: 'none',
              opacity: isLoaded ? 1 : 0,
              transition: 'opacity 0.3s var(--tiq-ease)',
              WebkitTouchCallout: 'none',
              userSelect: 'none',
            }}
          />
        )}

        {/* hover overlay: filename (mono) + favourite + download.
            showActions=false (e.g. read-only portfolio) → just a subtle darken,
            no exposed filename or action buttons. */}
        <div className="absolute inset-0 flex items-end justify-between opacity-0 group-hover:opacity-100" style={{ background: overlayBg, transition: 'opacity 0.2s var(--tiq-ease)', padding: 10 }}>
          {showActions && <span className="font-tiq-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.85)' }}>{photo.filename}</span>}
          {showActions && <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {onFavorite && (
              <button onClick={() => onFavorite(photo)} title="Favourite" style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill={isFav ? '#BF212F' : 'transparent'} stroke={isFav ? '#BF212F' : '#fff'} strokeWidth="2">
                  <path d="M12 21s-7-4.6-9.5-9C1 9 2.5 5.5 6 5.5c2 0 3.2 1.2 4 2.3.8-1.1 2-2.3 4-2.3 3.5 0 5 3.5 3.5 6.5C19 16.4 12 21 12 21z" />
                </svg>
              </button>
            )}
            {onDownload && (
              <button onClick={() => onDownload(photo)} title="Download" style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <path d="M12 3v12M7 11l5 5 5-5M5 21h14" />
                </svg>
              </button>
            )}
          </div>}
        </div>
      </div>
    );
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {columns.map((col, c) => (
          <div key={c} style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
            {col.map(({ photo, index }) => renderTile(photo, index))}
          </div>
        ))}
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="w-full h-20 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full animate-spin" style={{ border: '3px solid rgba(191,33,47,0.25)', borderTopColor: '#BF212F' }} />
        </div>
      )}
    </>
  );
}

export default MasonryGrid;
